import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useSession } from "../lib/auth";
import type { Role } from "../lib/permissions";
import { ROLE_LABEL } from "../lib/permissions";

interface Props {
  children: ReactNode;
  requireRole?: Role | Role[];
}

export function ProtectedRoute({ children, requireRole }: Props): JSX.Element {
  const { user, loading } = useSession();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg">
        <div className="text-sm text-text-muted">Loading…</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to={`/auth/signin?from=${encodeURIComponent(location.pathname)}`} replace />;
  }

  if (requireRole !== undefined) {
    const required: Role[] = Array.isArray(requireRole) ? requireRole : [requireRole];
    if (!required.includes(user.role)) {
      const labels = required.map((r) => ROLE_LABEL[r]).join(" or ");
      return (
        <div className="min-h-screen flex items-center justify-center bg-bg px-6">
          <div className="card max-w-md w-full p-6 text-center">
            <div className="text-3xl mb-2" aria-hidden>🔒</div>
            <h1 className="text-lg font-semibold text-text">403 — Not authorized</h1>
            <p className="mt-2 text-sm text-text-muted">
              This area is only available to {labels}. Ask an admin if you need access.
            </p>
            <a
              href="/"
              className="mt-4 inline-flex h-11 items-center justify-center rounded-lg bg-accent px-4 text-sm font-medium text-white hover:opacity-90"
            >
              Back to home
            </a>
          </div>
        </div>
      );
    }
  }

  return <>{children}</>;
}
